'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { toasterApi, TABLE_IDS } from '@terra/ui-design-system';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEndEvent,
  CellClickedEvent,
  GridApi,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { registerRequestGridHandler } from '../../hooks/useReplayKeyboard';
import { useAppTargetId } from '../../store/projectStore';
import { useChatStore } from '../../store/chatStore';
import {
  SelectHttpContextForChatDialog,
  type SelectHttpContextForChatDialogState,
} from '../dialogs/SelectHttpContextForChatDialog';
import { ReassociateHarEntriesDialog } from '../dialogs/ReassociateHarEntriesDialog';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../../utils/httpMentionUtils';
import {
  getDefaultStatusMessage,
  stringifyMaybeJsonBody,
} from '../../utils/httpFormatter';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import { useLayoutStore } from '../../store/layoutStore';
import { useDecoder } from '../../hooks/useDecoder';
import { useComparer } from '../../hooks/useComparer';
import { useTqlFilter } from '../../hooks/useTqlFilter';
import { RequestsFilterBar } from './RequestsFilterBar';
import { RequestsContentUnavailable } from './RequestsContentUnavailable';
import { RequestsFilterEmptyState } from './RequestsFilterEmptyState';
import { createRequestsContentColumnDefs } from './requestsContentColumns';
import { RequestsContentGrid } from './RequestsContentGrid';
import { RequestsReassociateActionsBar } from './RequestsReassociateActionsBar';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useHarReassociateEnabled } from '../../hooks/useHarReassociateEnabled';
import { useTrackedHarById } from '../../hooks/api/useTrackedHarById';
import { useTrackedHarEntries } from '../../hooks/api/useTrackedHarEntries';
import { useHarFilterAutoPage } from '../../hooks/useHarFilterAutoPage';
import { useTrackedHarResponseBodySearch } from '../../hooks/api/useTrackedHarResponseBodySearch';
import { getRequestDetailsHarState } from '../../hooks/api/getRequestDetailsHarState';
import {
  mapHarEntryToHarRequest,
  type HARRequest,
} from '../../hooks/api/mapHarEntryToHarRequest';
import {
  resolveHarEntryRequestBodyText,
  resolveHarEntryResponseBodyText,
} from '@terra/shared-utils';
import { filterHarRequestsForView } from '../../utils/reassociate-har-entries/filter-har-requests-for-view';
import {
  isHarSelectAllEvent,
  isShiftClickEvent,
  rangeSelectIds,
  shouldApplyHarGridSelection,
  shouldFetchNextHarPage,
} from '../../utils/reassociate-har-entries/range-select-ids';
import { REQUESTS_CONTENT_CHECKBOX_COL_ID } from './requestsReassociateAutomationIds';

type SelectedEndpoint = {
  method: string;
  path: string;
  domain: string;
};

type RequestsContentProps = {
  selectedEndpoint?: SelectedEndpoint;
  endpointId?: string;
  tabId?: string;
};

export function RequestsContent({ endpointId, tabId }: RequestsContentProps) {
  const tFullHar = useTranslations('httpHistory.fullHar');
  const appTargetId = useAppTargetId();
  const { userId } = useCurrentUser();
  const isHarReassociateEnabled = useHarReassociateEnabled();
  const { appendToInput, sendHttpMentionsToChat } = useChatStore();
  const { openDecoderWith } = useDecoder();
  const { addRequestToComparer } = useComparer();
  const selectedRequestsContentId = useLayoutStore(
    (state) => state.selectedRequestsContentId,
  );
  const setSelectedRequestsContentId = useLayoutStore(
    (state) => state.setSelectedRequestsContentId,
  );
  const [httpContextDialogState, setHttpContextDialogState] =
    useState<SelectHttpContextForChatDialogState | null>(null);
  const [selectedHarIds, setSelectedHarIds] = useState<string[]>([]);
  const [isReassociateOpen, setIsReassociateOpen] = useState(false);
  const lastClickedHarIdRef = useRef<string | null>(null);
  const gridApiRef = useRef<GridApi<HARRequest> | null>(null);
  const selectedHarIdsRef = useRef(selectedHarIds);

  selectedHarIdsRef.current = selectedHarIds;
  const handleHttpContextDialogClose = useCallback(() => {
    setHttpContextDialogState(null);
  }, []);
  const {
    requests,
    totalCount,
    isLoading,
    error,
    fetchNextPage,
    fetchRemainingPages,
    hasNextPage,
    isFetchingNextPage,
  } = useTrackedHarEntries({
    applicationId: appTargetId,
    endpointId,
  });
  const {
    matchesFilters,
    hasActiveFilters,
    tqlQuery,
    setTqlQuery,
    filterMode,
    setFilterMode,
  } = useTqlFilter();
  const responseBodySearch = useTrackedHarResponseBodySearch({
    applicationId: appTargetId,
    requests,
    tqlQuery,
    filterMode,
  });
  const filteredRequests = useMemo(
    () =>
      filterHarRequestsForView({
        requests,
        hasActiveFilters,
        responseBodySearch,
        matchesFilters,
      }),
    [requests, hasActiveFilters, matchesFilters, responseBodySearch],
  );
  const selectedSlimRequest = useMemo(() => {
    if (!selectedRequestsContentId || !filteredRequests) return null;

    return (
      filteredRequests.find((r) => r.id === selectedRequestsContentId) || null
    );
  }, [selectedRequestsContentId, filteredRequests]);
  const {
    fetchTrackedHarById,
    harEntry: selectedFullHar,
    isLoading: isLoadingSelectedFullHar,
    isError: isSelectedFullHarError,
  } = useTrackedHarById({
    applicationId: appTargetId,
    harEntryId: selectedSlimRequest?.id,
  });
  const {
    selectedRequest: selectedFullRequest,
    isLoadingFullHar,
    isFullHarError,
  } = getRequestDetailsHarState({
    selectedSlimRequest,
    selectedFullHar,
    isLoading: isLoadingSelectedFullHar,
    isError: isSelectedFullHarError,
  });
  const setSelectedRequest = useCallback(
    (request: HARRequest | null) => {
      setSelectedRequestsContentId(request?.id || null);
    },
    [setSelectedRequestsContentId],
  );
  const tablePersistence = useMemo(
    () =>
      userId
        ? {
            tableId: TABLE_IDS.PROXY_HTTP_HISTORY,
            applicationId: appTargetId,
            userId,
          }
        : undefined,
    [appTargetId, userId],
  );
  const selectedRows = useMemo(
    () =>
      (filteredRequests ?? []).filter((request) =>
        selectedHarIds.includes(request.id),
      ),
    [filteredRequests, selectedHarIds],
  );
  const showReassociateBar = Boolean(endpointId) && isHarReassociateEnabled;
  const resolveFullHarRequest = useCallback(
    async (request: HARRequest): Promise<HARRequest> => {
      if (!appTargetId) {
        throw new Error('No application selected');
      }

      const fullHar = await fetchTrackedHarById({
        applicationId: appTargetId,
        harEntryId: request.id,
      });

      return mapHarEntryToHarRequest(fullHar);
    },
    [appTargetId, fetchTrackedHarById],
  );
  const handleAddToContext = useCallback(
    async (request: HARRequest) => {
      try {
        const fullRequest = await resolveFullHarRequest(request);
        const harEntry = fullRequest.grpcHarDisplay;
        const requestBody = resolveHarEntryRequestBodyText({
          harEntry,
          bodyView: 'decoded',
          fallbackBody: stringifyMaybeJsonBody(fullRequest.requestBody),
        });
        const mention = createHttpRequestMention({
          id: fullRequest.id,
          method: fullRequest.method,
          url: fullRequest.url,
          headers: fullRequest.headers.request,
          rawHeaders: fullRequest.rawHeaders.request,
          body: requestBody,
          httpVersion: fullRequest.httpVersion,
        });

        if (fullRequest.status > 0) {
          const responseBody = resolveHarEntryResponseBodyText({
            harEntry,
            bodyView: 'decoded',
            fallbackBody:
              typeof fullRequest.responseBody === 'string'
                ? fullRequest.responseBody
                : JSON.stringify(fullRequest.responseBody, null, 2),
          });

          setHttpContextDialogState({
            requestMention: mention,
            responseMention: createHttpResponseMention({
              id: fullRequest.id,
              method: fullRequest.method,
              url: fullRequest.url,
              statusCode: fullRequest.status,
              statusText: getDefaultStatusMessage(fullRequest.status),
              headers: fullRequest.headers.response,
              rawHeaders: fullRequest.rawHeaders.response,
              body: responseBody,
              httpVersion: fullRequest.responseHttpVersion,
            }),
          });

          return;
        }

        sendHttpMentionsToChat({
          mentions: [mention],
        });
      } catch (caughtError: unknown) {
        console.error('Failed to load full HAR for chat context:', caughtError);
        toasterApi.error({
          title: tFullHar('addToChatErrorTitle'),
          description: tFullHar('addToChatErrorDescription'),
        });
      }
    },
    [resolveFullHarRequest, sendHttpMentionsToChat, tFullHar],
  );
  const { openHarRequestInReplay } = useHttpReplay();
  const handleReplay = useCallback(
    async (request: HARRequest) => {
      try {
        const fullRequest = await resolveFullHarRequest(request);

        openHarRequestInReplay(fullRequest);
      } catch (caughtError: unknown) {
        console.error('Failed to load full HAR for replay:', caughtError);
        toasterApi.error({
          title: tFullHar('openReplayErrorTitle'),
          description: tFullHar('openReplayErrorDescription'),
        });
      }
    },
    [openHarRequestInReplay, resolveFullHarRequest, tFullHar],
  );

  useEffect(() => {
    if (!tabId) {
      return;
    }

    return registerRequestGridHandler({
      tabId,
      getSelectedRequest: () => selectedFullRequest ?? selectedSlimRequest,
      sendToReplay: (request) => {
        const req = request as HARRequest;

        handleReplay(req);
      },
    });
  }, [tabId, selectedFullRequest, selectedSlimRequest, handleReplay]);

  const applySelectedIds = useCallback((nextIds: string[]) => {
    setSelectedHarIds(nextIds);
    const api = gridApiRef.current;

    if (!api) {
      return;
    }

    api.forEachNode((node) => {
      if (node.data) {
        node.setSelected(nextIds.includes(node.data.id), false);
      }
    });
  }, []);
  const handleGridReady = useCallback((api: GridApi<HARRequest>) => {
    gridApiRef.current = api;
    const ids = selectedHarIdsRef.current;

    if (ids.length === 0) {
      return;
    }

    api.forEachNode((node) => {
      if (node.data) {
        node.setSelected(ids.includes(node.data.id), false);
      }
    });
  }, []);
  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<HARRequest>) => {
      const nextIds = event.api
        .getSelectedRows()
        .map((row) => row.id)
        .filter((id): id is string => Boolean(id));

      if (
        !shouldApplyHarGridSelection({
          source: event.source,
          nextIds,
          selectedIds: selectedHarIdsRef.current,
        })
      ) {
        return;
      }

      if (!isHarSelectAllEvent(event.source) || nextIds.length === 0) {
        setSelectedHarIds(nextIds);

        return;
      }

      void (async () => {
        const allRows = await fetchRemainingPages();
        const visible = filterHarRequestsForView({
          requests: allRows,
          hasActiveFilters,
          responseBodySearch,
          matchesFilters,
        });

        applySelectedIds((visible ?? []).map((row) => row.id));
      })();
    },
    [
      applySelectedIds,
      fetchRemainingPages,
      hasActiveFilters,
      matchesFilters,
      responseBodySearch,
    ],
  );
  const handleCellClicked = useCallback(
    (event: CellClickedEvent<HARRequest>) => {
      const row = event.data;

      if (!row) {
        return;
      }

      const columnId = event.column?.getColId();
      const nativeEvent = event.event;
      const isShiftClick = isShiftClickEvent(nativeEvent);

      if (columnId === REQUESTS_CONTENT_CHECKBOX_COL_ID) {
        lastClickedHarIdRef.current = row.id;

        return;
      }

      if (isShiftClick && endpointId && isHarReassociateEnabled) {
        const visibleIds = (filteredRequests ?? []).map((item) => item.id);
        const fromId = lastClickedHarIdRef.current ?? row.id;

        applySelectedIds(
          rangeSelectIds({ ids: visibleIds, fromId, toId: row.id }),
        );
        lastClickedHarIdRef.current = row.id;

        return;
      }

      setSelectedRequest(selectedSlimRequest?.id === row.id ? null : row);
    },
    [
      applySelectedIds,
      endpointId,
      filteredRequests,
      isHarReassociateEnabled,
      selectedSlimRequest,
      setSelectedRequest,
    ],
  );

  useHarFilterAutoPage({
    hasActiveFilters,
    hasNextPage,
    isFetchingNextPage,
    loadedCount: requests?.length ?? 0,
    error,
    fetchNextPage,
    errorTitle: tFullHar('detailsLoadErrorTitle'),
  });
  const handleBodyScrollEnd = useCallback(
    (event: BodyScrollEndEvent<HARRequest>) => {
      if (event.direction !== 'vertical') {
        return;
      }

      if (
        shouldFetchNextHarPage({
          lastDisplayedRowIndex: event.api.getLastDisplayedRowIndex(),
          displayedRowCount: event.api.getDisplayedRowCount(),
          hasNextPage,
          isFetchingNextPage,
        })
      ) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );
  const columnDefs = useMemo(
    () =>
      createRequestsContentColumnDefs({
        onAddToContext: handleAddToContext,
        onReplay: handleReplay,
        enableCheckboxSelection: Boolean(endpointId) && isHarReassociateEnabled,
      }),
    [endpointId, handleAddToContext, handleReplay, isHarReassociateEnabled],
  );

  if (!requests || requests.length === 0) {
    return (
      <RequestsContentUnavailable
        errorMessage={error?.message}
        requests={requests}
        endpointId={endpointId}
        isLoading={isLoading}
      />
    );
  }

  if (filteredRequests && filteredRequests.length === 0 && hasActiveFilters) {
    return (
      <RequestsFilterEmptyState
        tqlQuery={tqlQuery}
        setTqlQuery={setTqlQuery}
        filterMode={filterMode}
        setFilterMode={setFilterMode}
        isSearching={
          responseBodySearch.isSearching || isFetchingNextPage || hasNextPage
        }
      />
    );
  }

  return (
    <VStack h="full" w="full" gap="0">
      <RequestsFilterBar
        tqlQuery={tqlQuery}
        setTqlQuery={setTqlQuery}
        filterMode={filterMode}
        setFilterMode={setFilterMode}
        showScopeControls={false}
      />
      {showReassociateBar ? (
        <RequestsReassociateActionsBar
          selectedCount={selectedRows.length}
          sourceHarTotalCount={totalCount}
          onReassociate={() => setIsReassociateOpen(true)}
        />
      ) : null}
      <Box flex="1" w="full" overflow="hidden" minH="0">
        <VStack h="full" w="full" gap="0">
          <RequestsContentGrid
            columnDefs={columnDefs}
            rowData={filteredRequests ?? []}
            enableSelection={Boolean(endpointId) && isHarReassociateEnabled}
            selectedSlimRequest={selectedSlimRequest}
            selectedFullRequest={selectedFullRequest}
            isLoadingFullHar={isLoadingFullHar}
            isFullHarError={isFullHarError}
            applicationId={appTargetId}
            tablePersistence={tablePersistence}
            onGridReady={handleGridReady}
            onCellClicked={handleCellClicked}
            onSelectionChanged={handleSelectionChanged}
            onBodyScrollEnd={handleBodyScrollEnd}
            onCloseDetails={() => setSelectedRequest(null)}
            onReplay={handleReplay}
            openDecoderWith={openDecoderWith}
            addRequestToComparer={addRequestToComparer}
            appendToInput={appendToInput}
            sendHttpMentionsToChat={sendHttpMentionsToChat}
          />
        </VStack>
      </Box>
      {endpointId && appTargetId && isHarReassociateEnabled ? (
        <ReassociateHarEntriesDialog
          isOpen={isReassociateOpen}
          applicationId={appTargetId}
          sourceEndpointId={endpointId}
          selectedRows={selectedRows}
          sourceHarTotalCount={totalCount}
          onOpenChange={setIsReassociateOpen}
          onMoved={() => setSelectedHarIds([])}
        />
      ) : null}
      <SelectHttpContextForChatDialog
        state={httpContextDialogState}
        onClose={handleHttpContextDialogClose}
      />
    </VStack>
  );
}
